/**
 * Platform console strings.
 *
 * English is the source of truth: `StringKey` is derived from it, so adding a key
 * here and nowhere else is a type error in every other locale — a missing
 * translation cannot be discovered by a user first.
 *
 * FRENCH and SWAHILI follow the wording already used in KargoLink's console, so an
 * operator moving between Baraka products reads the same terms for the same ideas.
 *
 * One Lingala pair is deliberately NOT identical: "Active" is `Ezali kosala` for
 * an operator (a thing) and `Azali kosala` for a member of staff (a person),
 * because Lingala marks that distinction in the verb prefix. A consistency sweep
 * that flattened them would be wrong.
 *
 * LINGALA IS A DRAFT AND NEEDS A NATIVE-SPEAKER REVIEW before it is put in front
 * of anyone. It is written in the Kinshasa register, keeps the widely-borrowed
 * French terms that Lingala speakers actually use for banking and admin words
 * (facture, komanda), and leaves anything genuinely uncertain to fall back to
 * English rather than guessing. Nothing renders broken while it is incomplete:
 * `useT` falls back to English per key.
 */

export const LOCALES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'sw', label: 'Kiswahili', short: 'SW' },
  { code: 'ln', label: 'Lingála', short: 'LN' },
] as const;

export type LocaleCode = (typeof LOCALES)[number]['code'];
export type StringKey = keyof (typeof dict)['en'];

export const dict = {
  en: {
    'nav.routes': 'Routes',
    'routes.title': 'Route performance',
    'routes.subtitle': 'Which routes are working, across every operator.',
    'routes.departed': 'Departed',
    'routes.selling': 'Selling now',
    'routes.windowHint.departed': 'Trips that have run, in the last {days} days. Load factor is final.',
    'routes.windowHint.selling': 'Trips still on sale, in the next {days} days. Load factor is how full they are so far.',
    'routes.colRoute': 'Route',
    'routes.colOperator': 'Operator',
    'routes.colDepartures': 'Departures',
    'routes.colLoad': 'Load',
    'routes.colSeats': 'Seats',
    'routes.colGmv': 'Gross value',
    'routes.colCommission': 'Commission',
    'routes.emptyTitle': 'No departures in this window',
    'routes.emptyBody': 'Once trips run, their load factor and revenue appear here.',
    'preview.title': 'Open their site',
    'preview.customer': 'Passenger site',
    'preview.admin': 'Operator back office',
    'common.filterStatus': 'Filter by status',
    'common.filterEntity': 'Filter by entity',
    'common.allStatuses': 'All statuses',
    'common.allEntities': 'All entities',
    'common.name': 'Name',
    'common.email': 'Email',
    'common.empty': 'Nothing here yet',
    'tenants.emptyTitle': 'No operators yet',
    'tenants.emptyBody': 'Create the first one to get started.',
    'tenants.colPlan': 'Plan',
    'tenants.colBookings': 'Bookings',
    'tenants.onboardingStarted': 'Onboarding started',
    'invoices.emptyTitle': 'No invoices yet',
    'invoices.emptyBody': 'They appear once a billing period is closed.',
    'invoices.subtitleLong': 'What operators owe the platform. Collection is handled outside the system in v1.',
    'audit.emptyTitle': 'Nothing recorded yet',
    'audit.emptyBody': 'Administrative actions appear here as they happen.',
    'audit.subtitleLong': 'Every administrative action, across the platform and its operators.',
    'staff.titleLong': 'Platform staff',
    'staff.subtitleLong': 'Who can administer the platform itself.',
    'staff.inviteTitle': 'Invite an administrator',
    'staff.colLastSignIn': 'Last signed in',
    'staff.emptyTitle': 'No administrators yet',
    'staff.emptyBody': 'Invite someone to share the load.',
    'staff.never': 'Never',
    // --- shell -------------------------------------------------------------
    'brand.suffix': 'Platform',
    'nav.section.operations': 'Operations',
    'nav.section.money': 'Money',
    'nav.section.governance': 'Governance',
    'nav.overview': 'Overview',
    'nav.tenants': 'Operators',
    'nav.invoices': 'Invoices',
    'nav.promotions': 'Promotions',
    'nav.audit': 'Audit',
    'nav.staff': 'Staff',
    'shell.account': 'Account',
    'shell.signOut': 'Sign out',
    'shell.language': 'Language',
    'shell.profile': 'Profile',
    'profile.title': 'Profile',
    'profile.heading': 'Your profile',
    'profile.hint': 'Your name and contact details, and the picture other staff see beside your name.',
    'profile.photo': 'Profile picture',
    'profile.upload': 'Upload a picture',
    'profile.replace': 'Replace picture',
    'profile.removePhoto': 'Remove picture',
    'profile.photoHint': 'PNG, JPEG or WebP, up to 2 MB. Square works best.',
    'profile.name': 'Full name',
    'profile.phone': 'Phone',
    'profile.phoneHint': 'Optional. Colleagues use it to reach you about a trip.',
    'profile.email': 'Email',
    'profile.emailLocked': 'Your email is how you sign in, so it is changed by an administrator rather than here.',
    'profile.save': 'Save changes',
    'profile.saved': 'Profile updated.',
    'profile.saveFailed': 'Could not save that. Try again.',
    'profile.photoFailed': 'That picture could not be uploaded.',
    'shell.openMenu': 'Open menu',
    'shell.closeMenu': 'Close menu',

    // --- notifications -----------------------------------------------------
    'notif.title': 'Needs attention',
    'notif.open': 'Open notifications',
    'notif.empty': 'Nothing needs you right now.',
    'notif.emptyHint': 'Onboarding operators, unpaid invoices and suspensions show up here.',
    'notif.onboarding': '{count} operator awaiting go-live',
    'notif.onboarding_other': '{count} operators awaiting go-live',
    'notif.onboardingHint': 'Not reachable by passengers until activated.',
    'notif.unpaid': '{count} invoice awaiting payment',
    'notif.unpaid_other': '{count} invoices awaiting payment',
    'notif.unpaidHint': 'Sent, not yet settled.',
    'notif.suspended': '{count} operator suspended',
    'notif.suspended_other': '{count} operators suspended',
    'notif.suspendedHint': 'Their booking site is showing a maintenance notice.',

    // --- common ------------------------------------------------------------
    'common.loading': 'Loading…',
    'common.search': 'Search',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.close': 'Close',
    'common.retry': 'Try again',
    'common.none': 'None',
    'common.all': 'All',

    // --- overview ----------------------------------------------------------
    'overview.title': 'Overview',
    'overview.subtitle': 'How the platform is doing, over the last 30 days.',
    'overview.activeTenants': 'Active operators',
    'overview.activeTenantsHint': '{onboarding} onboarding · {suspended} suspended',
    'overview.bookings': 'Bookings',
    'overview.bookingsHint': 'Across all operators',
    'overview.gmv': 'Gross booking value',
    'overview.gmvHint': 'What passengers paid',
    'overview.revenue': 'Platform revenue',
    'overview.revenueHint': 'Commission accrued',
    'overview.byTenant': 'By operator',
    'overview.colTenant': 'Operator',
    'overview.colBookings': 'Bookings',
    'overview.colGross': 'Gross value',
    'overview.colCommission': 'Our commission',
    'overview.emptyTitle': 'No confirmed bookings yet',
    'overview.emptyBody': 'Figures appear once an operator takes its first booking.',
    'nav.demo': 'Demo data',
    'demo.title': 'Demo data',
    'demo.subtitle': 'Seeded operators and the logins to demonstrate them with.',
    'demo.unavailable': 'Seeding is off for this deployment',
    'demo.password': 'Shared password',
    'demo.passwordHint': 'Every demo account below uses this password.',
    'demo.seed': 'Seed demo data',
    'demo.reseed': 'Rebuild from scratch',
    'demo.clear': 'Remove demo data',
    'demo.seeding': 'Building two years of activity…',
    'demo.clearing': 'Removing…',
    'demo.seeded': 'Seeded {count} operators.',
    'demo.cleared': 'Removed {count} operators.',
    'demo.empty': 'Nothing seeded yet',
    'demo.emptyBody': 'Seed the demo estate to get five operators with two years of trading.',
    'demo.colOperator': 'Operator',
    'demo.colRole': 'Role',
    'demo.colEmail': 'Sign in as',
    'demo.colLinks': 'Open',
    'demo.passengerApp': 'Passenger app',
    'demo.adminApp': 'Operator admin',
    'demo.copy': 'Copy',
    'demo.copied': 'Copied',
    'demo.confirmClear': 'Remove every demo operator and all of its bookings?',
    'demo.confirmReseed': 'Rebuild the demo estate? Existing demo data is removed first.',
    'demo.warning': 'Demo accounts are real accounts with a published password. They exist only on sandbox, dev and UAT, and this page is refused everywhere else.',
    'overview.trendTitle': 'Bookings by month',
    'overview.trendHint': 'Confirmed bookings across every operator.',
    'overview.trendSeries': 'Bookings per month',
    'overview.seatsTitle': 'Seats sold by month',
    'overview.seatsHint': 'Seats, not bookings — a full coach and a busy sales day are different facts.',
    'overview.seatsSeries': 'Seats per month',
    'overview.perOperator': 'Operators by volume',
    'overview.perOperatorHint': 'Ranked by bookings, which compare across operators. Commission stays in each one\'s own currency — bar lengths never encode money.',
    'overview.window12': '12 months',
    'overview.window24': '24 months',
    'overview.mixed': 'Mixed currencies',
    'overview.mixedHint': 'Operators bill in different currencies, so this is not totalled.',
    'overview.noActivity': 'No activity in this window',
    'overview.colTrend': 'Trend',

    // --- tenants -----------------------------------------------------------
    'tenants.title': 'Operators',
    'tenants.subtitle': 'Every bus company on the platform.',
    'tenants.new': 'New operator',
    'tenants.colName': 'Operator',
    'tenants.colStatus': 'Status',
    'tenants.colLocale': 'Locale',
    'tenants.colCreated': 'Created',
    'tenants.status.ACTIVE': 'Active',
    'tenants.status.ONBOARDING': 'Onboarding',
    'tenants.status.SUSPENDED': 'Suspended',

    // --- create wizard -----------------------------------------------------
    'wizard.title': 'New operator',
    'wizard.step.company': 'Company',
    'wizard.step.owner': 'Owner',
    'wizard.step.contract': 'Contract',
    'wizard.companyName': 'Company name',
    'wizard.subdomain': 'Subdomain',
    'wizard.subdomainHint': 'Their booking site lives at this address. It cannot be changed later.',
    'wizard.subdomainPreview': 'Their booking site: {host} — this cannot be changed later.',
    'wizard.timezone': 'Timezone',
    'wizard.currency': 'Currency',
    'wizard.ownerName': 'Owner name',
    'wizard.ownerEmail': 'Owner email',
    'wizard.contractType': 'Contract type',
    'wizard.commission': 'Commission (%)',
    'wizard.monthlyFee': 'Monthly fee',
    'wizard.effectiveFrom': 'Effective from',
    'wizard.create': 'Create operator',
    'wizard.creating': 'Creating…',
    'wizard.createdTitle': 'Operator created',
    'wizard.createdBody': 'We emailed an invitation to {email}.',
    'wizard.failed': 'Could not create this operator.',

    // --- invoices ----------------------------------------------------------
    'invoices.title': 'Invoices',
    'invoices.subtitle': 'Platform fees billed to operators.',
    'invoices.colNumber': 'Number',
    'invoices.colTenant': 'Operator',
    'invoices.colPeriod': 'Period',
    'invoices.colTotal': 'Total',
    'invoices.colStatus': 'Status',
    'invoices.download': 'Download PDF',

    // --- audit -------------------------------------------------------------
    'audit.title': 'Audit',
    'audit.subtitle': 'Every change made from this console.',
    'audit.colWhen': 'When',
    'audit.colWho': 'Who',
    'audit.colAction': 'Action',
    'audit.colEntity': 'Entity',

    // --- staff -------------------------------------------------------------
    'staff.title': 'Staff',
    'staff.subtitle': 'Who can administer the platform.',
    'staff.invite': 'Invite administrator',
    'staff.colName': 'Name',
    'staff.colEmail': 'Email',
    'staff.colStatus': 'Status',
    'staff.active': 'Active',
    'staff.disabled': 'Disabled',

    // --- sign in -----------------------------------------------------------
    'signIn.title': 'Sign in',
    'signIn.subtitle': 'BusBook platform console',
    'signIn.email': 'Email',
    'signIn.password': 'Password',
    'signIn.submit': 'Sign in',
    'signIn.working': 'Signing in…',
    'signIn.failed': 'Email or password is incorrect.',
  },

  fr: {
    'nav.routes': 'Lignes',
    'routes.title': 'Performance des lignes',
    'routes.subtitle': 'Quelles lignes fonctionnent, chez tous les transporteurs.',
    'routes.departed': 'Parties',
    'routes.selling': 'En vente',
    'routes.windowHint.departed': 'Trajets effectués, sur les {days} derniers jours. Le taux de remplissage est définitif.',
    'routes.windowHint.selling': 'Trajets encore en vente, sur les {days} prochains jours. Le taux indique le remplissage à ce jour.',
    'routes.colRoute': 'Ligne',
    'routes.colOperator': 'Transporteur',
    'routes.colDepartures': 'Départs',
    'routes.colLoad': 'Remplissage',
    'routes.colSeats': 'Places',
    'routes.colGmv': 'Valeur brute',
    'routes.colCommission': 'Commission',
    'routes.emptyTitle': 'Aucun départ sur cette période',
    'routes.emptyBody': 'Dès que des trajets sont effectués, leur remplissage et leurs recettes apparaissent ici.',
    'preview.title': 'Ouvrir leur site',
    'preview.customer': 'Site des passagers',
    'preview.admin': 'Back-office du transporteur',
    'common.filterStatus': 'Filtrer par statut',
    'common.filterEntity': 'Filtrer par entité',
    'common.allStatuses': 'Tous les statuts',
    'common.allEntities': 'Toutes les entités',
    'common.name': 'Nom',
    'common.email': 'E-mail',
    'common.empty': 'Rien pour le moment',
    'tenants.emptyTitle': 'Aucun transporteur',
    'tenants.emptyBody': 'Créez le premier pour commencer.',
    'tenants.colPlan': 'Formule',
    'tenants.colBookings': 'Réservations',
    'tenants.onboardingStarted': 'Intégration démarrée',
    'invoices.emptyTitle': 'Aucune facture',
    'invoices.emptyBody': 'Elles apparaissent à la clôture d’une période de facturation.',
    'invoices.subtitleLong': 'Ce que les transporteurs doivent à la plateforme. Le recouvrement est géré hors système en v1.',
    'audit.emptyTitle': 'Rien d’enregistré',
    'audit.emptyBody': 'Les actions administratives apparaissent ici au fil de l’eau.',
    'audit.subtitleLong': 'Chaque action administrative, sur la plateforme et ses transporteurs.',
    'staff.titleLong': 'Équipe de la plateforme',
    'staff.subtitleLong': 'Qui peut administrer la plateforme elle-même.',
    'staff.inviteTitle': 'Inviter un administrateur',
    'staff.colLastSignIn': 'Dernière connexion',
    'staff.emptyTitle': 'Aucun administrateur',
    'staff.emptyBody': 'Invitez quelqu’un pour partager la charge.',
    'staff.never': 'Jamais',
    'brand.suffix': 'Plateforme',
    'nav.section.operations': 'Exploitation',
    'nav.section.money': 'Finances',
    'nav.section.governance': 'Gouvernance',
    'nav.overview': "Vue d'ensemble",
    'nav.tenants': 'Transporteurs',
    'nav.invoices': 'Factures',
    'nav.promotions': 'Promotions',
    'nav.audit': 'Audit',
    'nav.staff': 'Équipe',
    'shell.account': 'Compte',
    'shell.signOut': 'Se déconnecter',
    'shell.language': 'Langue',
    'shell.profile': 'Profil',
    'profile.title': 'Profil',
    'profile.heading': 'Votre profil',
    'profile.hint': 'Vos nom et coordonnées, et la photo que vos collègues voient à côté de votre nom.',
    'profile.photo': 'Photo de profil',
    'profile.upload': 'Téléverser une photo',
    'profile.replace': 'Remplacer la photo',
    'profile.removePhoto': 'Retirer la photo',
    'profile.photoHint': 'PNG, JPEG ou WebP, jusqu’à 2 Mo. Le carré rend le mieux.',
    'profile.name': 'Nom complet',
    'profile.phone': 'Téléphone',
    'profile.phoneHint': 'Facultatif. Vos collègues l’utilisent pour vous joindre au sujet d’un trajet.',
    'profile.email': 'E-mail',
    'profile.emailLocked': 'Votre e-mail sert à vous connecter ; il est modifié par un administrateur, pas ici.',
    'profile.save': 'Enregistrer',
    'profile.saved': 'Profil mis à jour.',
    'profile.saveFailed': 'Enregistrement impossible. Réessayez.',
    'profile.photoFailed': 'Cette photo n’a pas pu être téléversée.',
    'shell.openMenu': 'Ouvrir le menu',
    'shell.closeMenu': 'Fermer le menu',

    'notif.title': 'À traiter',
    'notif.open': 'Ouvrir les notifications',
    'notif.empty': "Rien ne vous attend pour l'instant.",
    'notif.emptyHint':
      "Les transporteurs en cours d'intégration, les factures impayées et les suspensions apparaissent ici.",
    'notif.onboarding': '{count} transporteur en attente de mise en service',
    'notif.onboarding_other': '{count} transporteurs en attente de mise en service',
    'notif.onboardingHint': "Injoignable par les passagers tant qu'il n'est pas activé.",
    'notif.unpaid': '{count} facture en attente de paiement',
    'notif.unpaid_other': '{count} factures en attente de paiement',
    'notif.unpaidHint': 'Envoyée, pas encore réglée.',
    'notif.suspended': '{count} transporteur suspendu',
    'notif.suspended_other': '{count} transporteurs suspendus',
    'notif.suspendedHint': 'Leur site affiche un avis de maintenance.',

    'common.loading': 'Chargement…',
    'common.search': 'Rechercher',
    'common.cancel': 'Annuler',
    'common.save': 'Enregistrer',
    'common.close': 'Fermer',
    'common.retry': 'Réessayer',
    'common.none': 'Aucun',
    'common.all': 'Tous',

    'overview.title': "Vue d'ensemble",
    'overview.subtitle': 'La santé de la plateforme sur les 30 derniers jours.',
    'overview.activeTenants': 'Transporteurs actifs',
    'overview.activeTenantsHint': '{onboarding} en intégration · {suspended} suspendus',
    'overview.bookings': 'Réservations',
    'overview.bookingsHint': 'Tous transporteurs confondus',
    'overview.gmv': 'Valeur brute des réservations',
    'overview.gmvHint': 'Ce que les passagers ont payé',
    'overview.revenue': 'Revenus de la plateforme',
    'overview.revenueHint': 'Commission accumulée',
    'overview.byTenant': 'Par transporteur',
    'overview.colTenant': 'Transporteur',
    'overview.colBookings': 'Réservations',
    'overview.colGross': 'Valeur brute',
    'overview.colCommission': 'Notre commission',
    'overview.emptyTitle': 'Aucune réservation confirmée',
    'overview.emptyBody': "Les chiffres apparaissent dès la première réservation d'un transporteur.",
    'nav.demo': 'Données de démo',
    'demo.title': 'Données de démo',
    'demo.subtitle': 'Transporteurs générés et identifiants pour les présenter.',
    'demo.unavailable': 'La génération est désactivée sur ce déploiement',
    'demo.password': 'Mot de passe commun',
    'demo.passwordHint': 'Tous les comptes de démo ci-dessous utilisent ce mot de passe.',
    'demo.seed': 'Générer les données',
    'demo.reseed': 'Tout regénérer',
    'demo.clear': 'Supprimer les données',
    'demo.seeding': 'Génération de deux ans d’activité…',
    'demo.clearing': 'Suppression…',
    'demo.seeded': '{count} transporteurs générés.',
    'demo.cleared': '{count} transporteurs supprimés.',
    'demo.empty': 'Rien de généré pour l’instant',
    'demo.emptyBody': 'Générez la démo pour obtenir cinq transporteurs et deux ans d’activité.',
    'demo.colOperator': 'Transporteur',
    'demo.colRole': 'Rôle',
    'demo.colEmail': 'Se connecter en tant que',
    'demo.colLinks': 'Ouvrir',
    'demo.passengerApp': 'Application passagers',
    'demo.adminApp': 'Admin transporteur',
    'demo.copy': 'Copier',
    'demo.copied': 'Copié',
    'demo.confirmClear': 'Supprimer tous les transporteurs de démo et leurs réservations ?',
    'demo.confirmReseed': 'Regénérer la démo ? Les données existantes sont d’abord supprimées.',
    'demo.warning': 'Les comptes de démo sont de vrais comptes avec un mot de passe publié. Ils n’existent que sur sandbox, dev et UAT ; cette page est refusée ailleurs.',
    'overview.trendTitle': 'Réservations par mois',
    'overview.trendHint': 'Réservations confirmées, tous transporteurs confondus.',
    'overview.trendSeries': 'Réservations par mois',
    'overview.seatsTitle': 'Places vendues par mois',
    'overview.seatsHint': 'Des places, pas des réservations : un car plein et une journée chargée sont deux faits différents.',
    'overview.seatsSeries': 'Places par mois',
    'overview.perOperator': 'Transporteurs par volume',
    'overview.perOperatorHint': 'Classés par réservations, comparables d\'un transporteur à l\'autre. La commission reste dans la monnaie de chacun : les barres n\'encodent jamais l\'argent.',
    'overview.window12': '12 mois',
    'overview.window24': '24 mois',
    'overview.mixed': 'Monnaies multiples',
    'overview.mixedHint': 'Les transporteurs facturent dans des monnaies différentes ; aucun total n\'est affiché.',
    'overview.noActivity': 'Aucune activité sur cette période',
    'overview.colTrend': 'Tendance',

    'tenants.title': 'Transporteurs',
    'tenants.subtitle': 'Toutes les compagnies de bus de la plateforme.',
    'tenants.new': 'Nouveau transporteur',
    'tenants.colName': 'Transporteur',
    'tenants.colStatus': 'Statut',
    'tenants.colLocale': 'Paramètres régionaux',
    'tenants.colCreated': 'Créé le',
    'tenants.status.ACTIVE': 'Actif',
    'tenants.status.ONBOARDING': 'En intégration',
    'tenants.status.SUSPENDED': 'Suspendu',

    'wizard.title': 'Nouveau transporteur',
    'wizard.step.company': 'Société',
    'wizard.step.owner': 'Responsable',
    'wizard.step.contract': 'Contrat',
    'wizard.companyName': 'Nom de la société',
    'wizard.subdomain': 'Sous-domaine',
    'wizard.subdomainHint':
      'Leur site de réservation vit à cette adresse. Elle ne pourra pas être modifiée.',
    'wizard.subdomainPreview': 'Leur site de réservation : {host} — non modifiable par la suite.',
    'wizard.timezone': 'Fuseau horaire',
    'wizard.currency': 'Devise',
    'wizard.ownerName': 'Nom du responsable',
    'wizard.ownerEmail': 'E-mail du responsable',
    'wizard.contractType': 'Type de contrat',
    'wizard.commission': 'Commission (%)',
    'wizard.monthlyFee': 'Forfait mensuel',
    'wizard.effectiveFrom': 'À compter du',
    'wizard.create': 'Créer le transporteur',
    'wizard.creating': 'Création…',
    'wizard.createdTitle': 'Transporteur créé',
    'wizard.createdBody': 'Nous avons envoyé une invitation à {email}.',
    'wizard.failed': 'Impossible de créer ce transporteur.',

    'invoices.title': 'Factures',
    'invoices.subtitle': 'Frais de plateforme facturés aux transporteurs.',
    'invoices.colNumber': 'Numéro',
    'invoices.colTenant': 'Transporteur',
    'invoices.colPeriod': 'Période',
    'invoices.colTotal': 'Total',
    'invoices.colStatus': 'Statut',
    'invoices.download': 'Télécharger le PDF',

    'audit.title': 'Audit',
    'audit.subtitle': 'Chaque modification faite depuis cette console.',
    'audit.colWhen': 'Quand',
    'audit.colWho': 'Qui',
    'audit.colAction': 'Action',
    'audit.colEntity': 'Entité',

    'staff.title': 'Équipe',
    'staff.subtitle': 'Qui peut administrer la plateforme.',
    'staff.invite': 'Inviter un administrateur',
    'staff.colName': 'Nom',
    'staff.colEmail': 'E-mail',
    'staff.colStatus': 'Statut',
    'staff.active': 'Actif',
    'staff.disabled': 'Désactivé',

    'signIn.title': 'Connexion',
    'signIn.subtitle': 'Console de la plateforme BusBook',
    'signIn.email': 'E-mail',
    'signIn.password': 'Mot de passe',
    'signIn.submit': 'Se connecter',
    'signIn.working': 'Connexion…',
    'signIn.failed': 'E-mail ou mot de passe incorrect.',
  },

  sw: {
    'nav.routes': 'Njia',
    'routes.title': 'Utendaji wa njia',
    'routes.subtitle': 'Njia zipi zinafanya kazi, kwa wasafirishaji wote.',
    'routes.departed': 'Zilizoondoka',
    'routes.selling': 'Zinauzwa',
    'routes.windowHint.departed': 'Safari zilizofanyika, katika siku {days} zilizopita. Kiwango cha kujaa ni cha mwisho.',
    'routes.windowHint.selling': 'Safari bado zinauzwa, katika siku {days} zijazo. Kiwango kinaonyesha zimejaa kiasi gani hadi sasa.',
    'routes.colRoute': 'Njia',
    'routes.colOperator': 'Msafirishaji',
    'routes.colDepartures': 'Safari',
    'routes.colLoad': 'Kujaa',
    'routes.colSeats': 'Viti',
    'routes.colGmv': 'Thamani jumla',
    'routes.colCommission': 'Kamisheni',
    'routes.emptyTitle': 'Hakuna safari katika kipindi hiki',
    'routes.emptyBody': 'Safari zikishafanyika, kujaa kwake na mapato huonekana hapa.',
    'preview.title': 'Fungua tovuti yao',
    'preview.customer': 'Tovuti ya abiria',
    'preview.admin': 'Ofisi ya msafirishaji',
    'common.filterStatus': 'Chuja kwa hali',
    'common.filterEntity': 'Chuja kwa kipengele',
    'common.allStatuses': 'Hali zote',
    'common.allEntities': 'Vipengele vyote',
    'common.name': 'Jina',
    'common.email': 'Barua pepe',
    'common.empty': 'Hakuna kitu bado',
    'tenants.emptyTitle': 'Hakuna wasafirishaji bado',
    'tenants.emptyBody': 'Unda wa kwanza ili kuanza.',
    'tenants.colPlan': 'Mpango',
    'tenants.colBookings': 'Nafasi',
    'tenants.onboardingStarted': 'Kujiunga kumeanza',
    'invoices.emptyTitle': 'Hakuna ankara bado',
    'invoices.emptyBody': 'Zinaonekana kipindi cha malipo kinapofungwa.',
    'invoices.subtitleLong': 'Kile wasafirishaji wanadaiwa na jukwaa. Ukusanyaji unafanywa nje ya mfumo katika v1.',
    'audit.emptyTitle': 'Hakuna kilichorekodiwa',
    'audit.emptyBody': 'Vitendo vya usimamizi vinaonekana hapa vinapotokea.',
    'audit.subtitleLong': 'Kila kitendo cha usimamizi, kwenye jukwaa na wasafirishaji wake.',
    'staff.titleLong': 'Wafanyakazi wa jukwaa',
    'staff.subtitleLong': 'Nani anaweza kusimamia jukwaa lenyewe.',
    'staff.inviteTitle': 'Alika msimamizi',
    'staff.colLastSignIn': 'Aliingia mwisho',
    'staff.emptyTitle': 'Hakuna wasimamizi bado',
    'staff.emptyBody': 'Mwalike mtu ashiriki majukumu.',
    'staff.never': 'Hajawahi',
    'brand.suffix': 'Jukwaa',
    'nav.section.operations': 'Uendeshaji',
    'nav.section.money': 'Fedha',
    'nav.section.governance': 'Usimamizi',
    'nav.overview': 'Muhtasari',
    'nav.tenants': 'Wasafirishaji',
    'nav.invoices': 'Ankara',
    'nav.promotions': 'Promotions',
    'nav.audit': 'Ukaguzi',
    'nav.staff': 'Wafanyakazi',
    'shell.account': 'Akaunti',
    'shell.signOut': 'Toka',
    'shell.language': 'Lugha',
    'shell.profile': 'Wasifu',
    'profile.title': 'Wasifu',
    'profile.heading': 'Wasifu wako',
    'profile.hint': 'Jina na mawasiliano yako, na picha wafanyakazi wenzako wanayoiona kando ya jina lako.',
    'profile.photo': 'Picha ya wasifu',
    'profile.upload': 'Pakia picha',
    'profile.replace': 'Badilisha picha',
    'profile.removePhoto': 'Ondoa picha',
    'profile.photoHint': 'PNG, JPEG au WebP, hadi MB 2. Mraba hufaa zaidi.',
    'profile.name': 'Jina kamili',
    'profile.phone': 'Simu',
    'profile.phoneHint': 'Si lazima. Wenzako huitumia kukufikia kuhusu safari.',
    'profile.email': 'Barua pepe',
    'profile.emailLocked': 'Barua pepe yako ndiyo unayotumia kuingia, hivyo hubadilishwa na msimamizi, si hapa.',
    'profile.save': 'Hifadhi mabadiliko',
    'profile.saved': 'Wasifu umesasishwa.',
    'profile.saveFailed': 'Imeshindwa kuhifadhi. Jaribu tena.',
    'profile.photoFailed': 'Picha hiyo haikuweza kupakiwa.',
    'shell.openMenu': 'Fungua menyu',
    'shell.closeMenu': 'Funga menyu',

    'notif.title': 'Yanahitaji uangalizi',
    'notif.open': 'Fungua arifa',
    'notif.empty': 'Hakuna linalokusubiri kwa sasa.',
    'notif.emptyHint':
      'Wasafirishaji wanaojiunga, ankara ambazo hazijalipwa na kusimamishwa huonekana hapa.',
    'notif.onboarding': 'Msafirishaji {count} anasubiri kuanza kazi',
    'notif.onboarding_other': 'Wasafirishaji {count} wanasubiri kuanza kazi',
    'notif.onboardingHint': 'Hawapatikani kwa abiria hadi waamilishwe.',
    'notif.unpaid': 'Ankara {count} inasubiri malipo',
    'notif.unpaid_other': 'Ankara {count} zinasubiri malipo',
    'notif.unpaidHint': 'Imetumwa, bado haijalipwa.',
    'notif.suspended': 'Msafirishaji {count} amesimamishwa',
    'notif.suspended_other': 'Wasafirishaji {count} wamesimamishwa',
    'notif.suspendedHint': 'Tovuti yao inaonyesha taarifa ya matengenezo.',

    'common.loading': 'Inapakia…',
    'common.search': 'Tafuta',
    'common.cancel': 'Ghairi',
    'common.save': 'Hifadhi',
    'common.close': 'Funga',
    'common.retry': 'Jaribu tena',
    'common.none': 'Hakuna',
    'common.all': 'Zote',

    'overview.title': 'Muhtasari',
    'overview.subtitle': 'Hali ya jukwaa katika siku 30 zilizopita.',
    'overview.activeTenants': 'Wasafirishaji hai',
    'overview.activeTenantsHint': '{onboarding} wanajiunga · {suspended} wamesimamishwa',
    'overview.bookings': 'Nafasi zilizowekwa',
    'overview.bookingsHint': 'Kwa wasafirishaji wote',
    'overview.gmv': 'Thamani jumla ya nafasi',
    'overview.gmvHint': 'Kiasi abiria walicholipa',
    'overview.revenue': 'Mapato ya jukwaa',
    'overview.revenueHint': 'Kamisheni iliyokusanywa',
    'overview.byTenant': 'Kwa msafirishaji',
    'overview.colTenant': 'Msafirishaji',
    'overview.colBookings': 'Nafasi',
    'overview.colGross': 'Thamani jumla',
    'overview.colCommission': 'Kamisheni yetu',
    'overview.emptyTitle': 'Bado hakuna nafasi iliyothibitishwa',
    'overview.emptyBody': 'Takwimu huonekana msafirishaji anapopata nafasi yake ya kwanza.',
    'nav.demo': 'Data ya onyesho',
    'demo.title': 'Data ya onyesho',
    'demo.subtitle': 'Waendeshaji walioundwa na taarifa za kuingia za kuonyesha nao.',
    'demo.unavailable': 'Uundaji umezimwa kwa usakinishaji huu',
    'demo.password': 'Nenosiri la pamoja',
    'demo.passwordHint': 'Kila akaunti ya onyesho hapa chini hutumia nenosiri hili.',
    'demo.seed': 'Unda data ya onyesho',
    'demo.reseed': 'Jenga upya kutoka mwanzo',
    'demo.clear': 'Ondoa data ya onyesho',
    'demo.seeding': 'Inajenga shughuli za miaka miwili…',
    'demo.clearing': 'Inaondoa…',
    'demo.seeded': 'Waendeshaji {count} wameundwa.',
    'demo.cleared': 'Waendeshaji {count} wameondolewa.',
    'demo.empty': 'Hakuna kilichoundwa bado',
    'demo.emptyBody': 'Unda onyesho upate waendeshaji watano na miaka miwili ya biashara.',
    'demo.colOperator': 'Mwendeshaji',
    'demo.colRole': 'Wadhifa',
    'demo.colEmail': 'Ingia kama',
    'demo.colLinks': 'Fungua',
    'demo.passengerApp': 'Programu ya abiria',
    'demo.adminApp': 'Msimamizi wa mwendeshaji',
    'demo.copy': 'Nakili',
    'demo.copied': 'Imenakiliwa',
    'demo.confirmClear': 'Ondoa kila mwendeshaji wa onyesho na nafasi zake zote?',
    'demo.confirmReseed': 'Jenga upya onyesho? Data iliyopo huondolewa kwanza.',
    'demo.warning': 'Akaunti za onyesho ni akaunti halisi zenye nenosiri lililochapishwa. Zipo kwenye sandbox, dev na UAT pekee, na ukurasa huu hukataliwa kwingine.',
    'overview.trendTitle': 'Nafasi kwa mwezi',
    'overview.trendHint': 'Nafasi zilizothibitishwa kwa waendeshaji wote.',
    'overview.trendSeries': 'Nafasi kwa mwezi',
    'overview.seatsTitle': 'Viti vilivyouzwa kwa mwezi',
    'overview.seatsHint': 'Viti, si nafasi — basi lililojaa na siku ya mauzo mengi ni mambo tofauti.',
    'overview.seatsSeries': 'Viti kwa mwezi',
    'overview.perOperator': 'Waendeshaji kwa idadi',
    'overview.perOperatorHint': 'Zimepangwa kwa nafasi, zinazolinganishwa kati ya waendeshaji. Kamisheni inabaki kwa sarafu ya kila mmoja — urefu wa mstari hauwakilishi fedha.',
    'overview.window12': 'Miezi 12',
    'overview.window24': 'Miezi 24',
    'overview.mixed': 'Sarafu mchanganyiko',
    'overview.mixedHint': 'Waendeshaji hutoza kwa sarafu tofauti, hivyo jumla haionyeshwi.',
    'overview.noActivity': 'Hakuna shughuli katika kipindi hiki',
    'overview.colTrend': 'Mwelekeo',

    'tenants.title': 'Wasafirishaji',
    'tenants.subtitle': 'Kila kampuni ya mabasi kwenye jukwaa.',
    'tenants.new': 'Msafirishaji mpya',
    'tenants.colName': 'Msafirishaji',
    'tenants.colStatus': 'Hali',
    'tenants.colLocale': 'Mipangilio ya eneo',
    'tenants.colCreated': 'Aliundwa',
    'tenants.status.ACTIVE': 'Hai',
    'tenants.status.ONBOARDING': 'Anajiunga',
    'tenants.status.SUSPENDED': 'Amesimamishwa',

    'wizard.title': 'Msafirishaji mpya',
    'wizard.step.company': 'Kampuni',
    'wizard.step.owner': 'Mmiliki',
    'wizard.step.contract': 'Mkataba',
    'wizard.companyName': 'Jina la kampuni',
    'wizard.subdomain': 'Kikoa kidogo',
    'wizard.subdomainHint': 'Tovuti yao ya kuweka nafasi iko kwenye anwani hii. Haiwezi kubadilishwa.',
    'wizard.subdomainPreview': 'Tovuti yao: {host} — haiwezi kubadilishwa baadaye.',
    'wizard.timezone': 'Saa za eneo',
    'wizard.currency': 'Sarafu',
    'wizard.ownerName': 'Jina la mmiliki',
    'wizard.ownerEmail': 'Barua pepe ya mmiliki',
    'wizard.contractType': 'Aina ya mkataba',
    'wizard.commission': 'Kamisheni (%)',
    'wizard.monthlyFee': 'Ada ya mwezi',
    'wizard.effectiveFrom': 'Kuanzia',
    'wizard.create': 'Unda msafirishaji',
    'wizard.creating': 'Inaunda…',
    'wizard.createdTitle': 'Msafirishaji ameundwa',
    'wizard.createdBody': 'Tumetuma mwaliko kwa {email}.',
    'wizard.failed': 'Imeshindwa kuunda msafirishaji huyu.',

    'invoices.title': 'Ankara',
    'invoices.subtitle': 'Ada za jukwaa zilizotozwa wasafirishaji.',
    'invoices.colNumber': 'Nambari',
    'invoices.colTenant': 'Msafirishaji',
    'invoices.colPeriod': 'Kipindi',
    'invoices.colTotal': 'Jumla',
    'invoices.colStatus': 'Hali',
    'invoices.download': 'Pakua PDF',

    'audit.title': 'Ukaguzi',
    'audit.subtitle': 'Kila mabadiliko yaliyofanywa kutoka kwenye console hii.',
    'audit.colWhen': 'Lini',
    'audit.colWho': 'Nani',
    'audit.colAction': 'Kitendo',
    'audit.colEntity': 'Kipengele',

    'staff.title': 'Wafanyakazi',
    'staff.subtitle': 'Nani anaweza kusimamia jukwaa.',
    'staff.invite': 'Alika msimamizi',
    'staff.colName': 'Jina',
    'staff.colEmail': 'Barua pepe',
    'staff.colStatus': 'Hali',
    'staff.active': 'Hai',
    'staff.disabled': 'Amezimwa',

    'signIn.title': 'Ingia',
    'signIn.subtitle': 'Console ya jukwaa la BusBook',
    'signIn.email': 'Barua pepe',
    'signIn.password': 'Nenosiri',
    'signIn.submit': 'Ingia',
    'signIn.working': 'Inaingia…',
    'signIn.failed': 'Barua pepe au nenosiri si sahihi.',
  },

  ln: {
    'nav.routes': 'Banzela',
    'routes.title': 'Mosala ya banzela',
    'routes.subtitle': 'Banzela nini ezali kosala malamu, epai ya bamemi nyonso.',
    'routes.departed': 'Ekendeki',
    'routes.selling': 'Ezali kotekama',
    'routes.windowHint.departed': 'Mibembo esalemi, na mikolo {days} eleki. Kotonda ezali ya nsuka.',
    'routes.windowHint.selling': 'Mibembo ezali naino kotekama, na mikolo {days} ekoya. Kotonda elakisi ndenge etondi tii sikoyo.',
    'routes.colRoute': 'Nzela',
    'routes.colOperator': 'Momemi',
    'routes.colDepartures': 'Mibembo',
    'routes.colLoad': 'Kotonda',
    'routes.colSeats': 'Bakiti',
    'routes.colGmv': 'Motuya mobimba',
    'routes.colCommission': 'Komisiɔ',
    'routes.emptyTitle': 'Mobembo moko te na eleko oyo',
    'routes.emptyBody': 'Ntango mibembo esalemi, kotonda mpe mbongo na yango ekomonana awa.',
    'preview.title': 'Kofungola site na bango',
    'preview.customer': 'Site ya bapaya',
    'preview.admin': 'Biro ya momemi',
    'common.filterStatus': 'Kopona na ezalela',
    'common.filterEntity': 'Kopona na eloko',
    'common.allStatuses': 'Bazalela nyonso',
    'common.allEntities': 'Biloko nyonso',
    'common.name': 'Nkombo',
    'common.email': 'Imeli',
    'common.empty': 'Eloko te sikoyo',
    'tenants.emptyTitle': 'Bamemi bazali naino te',
    'tenants.emptyBody': 'Salá ya liboso mpo na kobanda.',
    'tenants.colPlan': 'Lolenge',
    'tenants.colBookings': 'Bokomisi',
    'tenants.onboardingStarted': 'Kokota ebandi',
    'invoices.emptyTitle': 'Bafaktire ezali naino te',
    'invoices.emptyBody': 'Ekomonana ntango eleko ya lifuti ekangami.',
    'invoices.subtitleLong': 'Oyo bamemi bazali na niongo epai ya plateforme. Kokongola esalemaka libanda ya sistɛmɛ na v1.',
    'audit.emptyTitle': 'Eloko ekomami naino te',
    'audit.emptyBody': 'Misala ya bokambi ekomonana awa ntango esalemi.',
    'audit.subtitleLong': 'Mosala nyonso ya bokambi, na plateforme mpe na bamemi na yango.',
    'staff.titleLong': 'Basali ya plateforme',
    'staff.subtitleLong': 'Banani bakoki kokamba plateforme yango moko.',
    'staff.inviteTitle': 'Kobyanga mokambi',
    'staff.colLastSignIn': 'Kokota ya nsuka',
    'staff.emptyTitle': 'Bakambi bazali naino te',
    'staff.emptyBody': 'Byanga moto mpo na kokabola mosala.',
    'staff.never': 'Naino te',
    'brand.suffix': 'Plateforme',
    'nav.section.operations': 'Mosala',
    'nav.section.money': 'Mbongo',
    'nav.section.governance': 'Bokengeli',
    'nav.overview': 'Botali ya mobimba',
    'nav.tenants': 'Bamemi',
    'nav.invoices': 'Bafaktire',
    'nav.promotions': 'Promotions',
    'nav.audit': 'Botali',
    'nav.staff': 'Basali',
    'shell.account': 'Kɔntɛ',
    'shell.signOut': 'Kobima',
    'shell.language': 'Monɔkɔ',
    'shell.profile': 'Profil',
    'profile.title': 'Profil',
    'profile.heading': 'Profil na yo',
    'profile.hint': 'Nkombo na yo mpe ba contacts, mpe foto oyo baninga bazali komona pembeni ya nkombo na yo.',
    'profile.photo': 'Foto ya profil',
    'profile.upload': 'Tinda foto',
    'profile.replace': 'Bongola foto',
    'profile.removePhoto': 'Longola foto',
    'profile.photoHint': 'PNG, JPEG to WebP, tii na 2 MB. Carré ebimaka malamu.',
    'profile.name': 'Nkombo mobimba',
    'profile.phone': 'Telefone',
    'profile.phoneHint': 'Ezali na ntina makasi te. Baninga basalelaka yango mpo na kobenga yo.',
    'profile.email': 'Email',
    'profile.emailLocked': 'Email na yo nde okotelaka; administrateur nde abongolaka yango, awa te.',
    'profile.save': 'Bomba mbongwana',
    'profile.saved': 'Profil ebongwani.',
    'profile.saveFailed': 'Kobomba elongi te. Meka lisusu.',
    'profile.photoFailed': 'Foto wana etindami te.',
    'shell.openMenu': 'Kofungola menu',
    'shell.closeMenu': 'Kokanga menu',

    'notif.title': 'Esengeli otala',
    'notif.open': 'Kofungola bansango',
    'notif.empty': 'Eloko te ezali kozela yo sikoyo.',
    'notif.emptyHint':
      'Bamemi oyo bazali kokota, bafaktire oyo efutami te, mpe bapemisami bakomonana awa.',
    'notif.onboarding': 'Momemi {count} azali kozela kobanda mosala',
    'notif.onboarding_other': 'Bamemi {count} bazali kozela kobanda mosala',
    'notif.onboardingHint': 'Bapaya bakoki kokuta bango te liboso ya kofungola.',
    'notif.unpaid': 'Faktire {count} ezali kozela lifuti',
    'notif.unpaid_other': 'Bafaktire {count} bazali kozela lifuti',
    'notif.unpaidHint': 'Etindami, kasi efutami naino te.',
    'notif.suspended': 'Momemi {count} apemisami',
    'notif.suspended_other': 'Bamemi {count} bapemisami',
    'notif.suspendedHint': 'Site na bango ezali kolakisa liyebisi ya kobongisa.',

    'common.loading': 'Ezali kofungwama…',
    'common.search': 'Koluka',
    'common.cancel': 'Kotika',
    'common.save': 'Kobomba',
    'common.close': 'Kokanga',
    'common.retry': 'Meka lisusu',
    'common.none': 'Eloko te',
    'common.all': 'Nyonso',

    'overview.title': 'Botali ya mobimba',
    'overview.subtitle': 'Ndenge plateforme ezali kotambola na mikolo 30 eleki.',
    'overview.activeTenants': 'Bamemi ya mosala',
    'overview.activeTenantsHint': '{onboarding} bazali kokota · {suspended} bapemisami',
    'overview.bookings': 'Bokomisi',
    'overview.bookingsHint': 'Na bamemi nyonso',
    'overview.gmv': 'Motuya mobimba ya bokomisi',
    'overview.gmvHint': 'Oyo bapaya bafutaki',
    'overview.revenue': 'Mbongo ya plateforme',
    'overview.revenueHint': 'Komisiɔ esangani',
    'overview.byTenant': 'Na momemi',
    'overview.colTenant': 'Momemi',
    'overview.colBookings': 'Bokomisi',
    'overview.colGross': 'Motuya mobimba',
    'overview.colCommission': 'Komisiɔ na biso',
    'overview.emptyTitle': 'Bokomisi endimami naino te',
    'overview.emptyBody': 'Mituya ekomonana ntango momemi azwi bokomisi ya liboso.',
    'nav.demo': 'Ba-donné ya elakiseli',
    'demo.title': 'Ba-donné ya elakiseli',
    'demo.subtitle': 'Batambwisi bakelami mpe bansango ya kokota mpo na kolakisa.',
    'demo.unavailable': 'Kokela ba-donné ekangami na esika oyo',
    'demo.password': 'Monkanda ya kokota ya lisanga',
    'demo.passwordHint': 'Konti nyonso ya elakiseli awa esalelaka monkanda oyo.',
    'demo.seed': 'Kela ba-donné ya elakiseli',
    'demo.reseed': 'Tonga lisusu banda ebandeli',
    'demo.clear': 'Longola ba-donné ya elakiseli',
    'demo.seeding': 'Ezali kotonga mosala ya mibu mibale…',
    'demo.clearing': 'Ezali kolongola…',
    'demo.seeded': 'Batambwisi {count} bakelami.',
    'demo.cleared': 'Batambwisi {count} balongolami.',
    'demo.empty': 'Eloko ekelami naino te',
    'demo.emptyBody': 'Kela elakiseli mpo na kozwa batambwisi mitano na mibu mibale ya mombongo.',
    'demo.colOperator': 'Motambwisi',
    'demo.colRole': 'Mokumba',
    'demo.colEmail': 'Kota lokola',
    'demo.colLinks': 'Fungola',
    'demo.passengerApp': 'Application ya bato',
    'demo.adminApp': 'Admin ya motambwisi',
    'demo.copy': 'Kopi',
    'demo.copied': 'Ekopiami',
    'demo.confirmClear': 'Longola motambwisi nyonso ya elakiseli na bokeyi na yango nyonso?',
    'demo.confirmReseed': 'Tonga elakiseli lisusu? Ba-donné ezali elongwaka liboso.',
    'demo.warning': 'Bakonti ya elakiseli ezali bakonti ya solo na monkanda ebimisami. Ezali kaka na sandbox, dev mpe UAT, mpe lokasa oyo eboyami bisika mosusu.',
    'overview.trendTitle': 'Bokeyi na sanza',
    'overview.trendHint': 'Bokeyi endimami epai ya batambwisi nyonso.',
    'overview.trendSeries': 'Bokeyi na sanza',
    'overview.seatsTitle': 'Bakiti etekami na sanza',
    'overview.seatsHint': 'Bakiti, bokeyi te — bisi etondi mpe mokolo ya koteka mingi ezali makambo mibale ekeseni.',
    'overview.seatsSeries': 'Bakiti na sanza',
    'overview.perOperator': 'Batambwisi na motango',
    'overview.perOperatorHint': 'Etandami na bokeyi, oyo ekokani kati ya batambwisi. Komisyo etikali na mbongo ya moko na moko — bolai ya bar elakisaka mbongo te.',
    'overview.window12': 'Sanza 12',
    'overview.window24': 'Sanza 24',
    'overview.mixed': 'Mbongo ekeseni',
    'overview.mixedHint': 'Batambwisi bafutisaka na mbongo ekeseni, yango wana totale elakisami te.',
    'overview.noActivity': 'Mosala ezali te na eleko oyo',
    'overview.colTrend': 'Bokei',

    'tenants.title': 'Bamemi',
    'tenants.subtitle': 'Kompani nyonso ya bisi na plateforme.',
    'tenants.new': 'Momemi ya sika',
    'tenants.colName': 'Momemi',
    'tenants.colStatus': 'Ezalela',
    'tenants.colLocale': 'Esika mpe monɔkɔ',
    'tenants.colCreated': 'Esalemaki',
    'tenants.status.ACTIVE': 'Ezali kosala',
    'tenants.status.ONBOARDING': 'Azali kokota',
    'tenants.status.SUSPENDED': 'Apemisami',

    'wizard.title': 'Momemi ya sika',
    'wizard.step.company': 'Kompani',
    'wizard.step.owner': 'Nkolo',
    'wizard.step.contract': 'Kontra',
    'wizard.companyName': 'Nkombo ya kompani',
    'wizard.subdomain': 'Sous-domaine',
    'wizard.subdomainHint': 'Site na bango ya bokomisi ezali na adrɛsi oyo. Ekoki kobongwana te.',
    'wizard.subdomainPreview': 'Site na bango: {host} — ekoki kobongwana te na nsima.',
    'wizard.timezone': 'Ngonga ya esika',
    'wizard.currency': 'Mbongo',
    'wizard.ownerName': 'Nkombo ya nkolo',
    'wizard.ownerEmail': 'Imeli ya nkolo',
    'wizard.contractType': 'Lolenge ya kontra',
    'wizard.commission': 'Komisiɔ (%)',
    'wizard.monthlyFee': 'Lifuti ya sanza',
    'wizard.effectiveFrom': 'Kobanda na',
    'wizard.create': 'Kosala momemi',
    'wizard.creating': 'Ezali kosalema…',
    'wizard.createdTitle': 'Momemi asalemi',
    'wizard.createdBody': 'Totindeli {email} libyangi.',
    'wizard.failed': 'Tokoki kosala momemi oyo te.',

    'invoices.title': 'Bafaktire',
    'invoices.subtitle': 'Mbongo ya plateforme oyo esengami na bamemi.',
    'invoices.colNumber': 'Nimero',
    'invoices.colTenant': 'Momemi',
    'invoices.colPeriod': 'Eleko',
    'invoices.colTotal': 'Motuya nyonso',
    'invoices.colStatus': 'Ezalela',
    'invoices.download': 'Kokitisa PDF',

    'audit.title': 'Botali',
    'audit.subtitle': 'Mbongwana nyonso esalemi na console oyo.',
    'audit.colWhen': 'Ntango',
    'audit.colWho': 'Nani',
    'audit.colAction': 'Likambo',
    'audit.colEntity': 'Eloko',

    'staff.title': 'Basali',
    'staff.subtitle': 'Banani bakoki kokamba plateforme.',
    'staff.invite': 'Kobyanga mokambi',
    'staff.colName': 'Nkombo',
    'staff.colEmail': 'Imeli',
    'staff.colStatus': 'Ezalela',
    'staff.active': 'Azali kosala',
    'staff.disabled': 'Akangami',

    'signIn.title': 'Kokota',
    'signIn.subtitle': 'Console ya plateforme BusBook',
    'signIn.email': 'Imeli',
    'signIn.password': 'Motindo ya kokota',
    'signIn.submit': 'Kokota',
    'signIn.working': 'Ezali kokota…',
    'signIn.failed': 'Imeli to motindo ya kokota ezali malamu te.',
  },
} as const;

/**
 * Every locale must carry every key. Without this, `dict` is just four unrelated
 * object literals and a translator's omission is only discovered by whoever is
 * reading the screen — which, for Lingala, would be a bus operator in Kinshasa.
 * This turns it into a compile error instead.
 */
const _everyLocaleIsComplete: Record<LocaleCode, Record<StringKey, string>> = dict;
void _everyLocaleIsComplete;
